/* ════════════════════════════════════════════════════════════════════════
   TRUST SIGNALS
   Shopify-style checkout reassurance for AB8 Beauty & Skin:
     • PaymentMethods   — recognizable Visa / Mastercard / Amex / Discover /
                          Apple Pay / PayPal card-brand badges (inline SVG, no
                          external assets, crisp on any background).
     • SecureCheckoutBar — a "Guaranteed safe & secure checkout" lock strip
                          with the payment row + Stripe encryption note.
     • GuaranteeMedallions — scalloped seal stamps (secure checkout,
                          money-back guarantee, guaranteed shipping).
   Drop into CheckoutPage (both address + Stripe steps) and the Footer.
   ════════════════════════════════════════════════════════════════════════ */

/* ── White rounded card chassis every brand badge sits on ─────────────────── */
function CardChassis({ children, bg = '#ffffff', title }) {
  return (
    <span
      title={title}
      className="inline-flex shrink-0 rounded-[5px] ring-1 ring-black/10 shadow-sm bg-white"
    >
      <svg viewBox="0 0 46 30" className="h-7 w-auto" role="img" aria-label={title}>
        <rect x="0" y="0" width="46" height="30" rx="5" fill={bg} />
        {children}
      </svg>
    </span>
  );
}

/* ── Individual card brands ───────────────────────────────────────────────── */
function VisaBadge() {
  return (
    <CardChassis title="Visa">
      <text x="23" y="20" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
        fontSize="12.5" fontWeight="700" fontStyle="italic" letterSpacing="0.5" fill="#1434CB">
        VISA
      </text>
    </CardChassis>
  );
}

function MastercardBadge() {
  return (
    <CardChassis title="Mastercard">
      <circle cx="19" cy="15" r="8.2" fill="#EB001B" />
      <circle cx="28" cy="15" r="8.2" fill="#F79E1B" />
      {/* overlap wedge */}
      <path d="M23.5 8.9a8.2 8.2 0 0 1 0 12.2 8.2 8.2 0 0 1 0-12.2z" fill="#FF5F00" />
    </CardChassis>
  );
}

function AmexBadge() {
  return (
    <CardChassis title="American Express" bg="#1F72CF">
      <text x="23" y="13" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
        fontSize="5.6" fontWeight="700" letterSpacing="0.3" fill="#ffffff">AMERICAN</text>
      <text x="23" y="21.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
        fontSize="5.6" fontWeight="700" letterSpacing="0.3" fill="#ffffff">EXPRESS</text>
    </CardChassis>
  );
}

function DiscoverBadge() {
  return (
    <CardChassis title="Discover">
      <circle cx="36.5" cy="9" r="3.4" fill="#FF6000" />
      <text x="21" y="18.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
        fontSize="6.4" fontWeight="800" letterSpacing="0.2" fill="#111827">
        DISC<tspan fill="#FF6000">O</tspan>VER
      </text>
    </CardChassis>
  );
}

function ApplePayBadge() {
  return (
    <CardChassis title="Apple Pay" bg="#000000">
      <g transform="translate(8.5,8.5) scale(0.62)" fill="#ffffff">
        {/* apple silhouette + leaf */}
        <path d="M13.6 8.6c0-1.6 1.3-2.4 1.4-2.5-0.8-1.1-1.9-1.3-2.4-1.3-1-0.1-1.9 0.6-2.4 0.6s-1.3-0.6-2.1-0.6c-1.1 0-2.1 0.6-2.6 1.6-1.2 2-0.3 5 0.8 6.6 0.6 0.8 1.2 1.7 2 1.7 0.8 0 1.1-0.5 2.1-0.5s1.3 0.5 2.1 0.5 1.4-0.8 1.9-1.6c0.6-0.9 0.9-1.8 0.9-1.9-0.1 0-1.7-0.7-1.7-2.6z" />
        <path d="M12.4 4.4c0.4-0.5 0.7-1.3 0.6-2-0.6 0-1.4 0.4-1.8 0.9-0.4 0.4-0.8 1.2-0.7 1.9 0.7 0.1 1.4-0.3 1.9-0.8z" />
      </g>
      <text x="27" y="20" fontFamily="Arial, Helvetica, sans-serif" fontSize="11"
        fontWeight="500" fill="#ffffff">Pay</text>
    </CardChassis>
  );
}

function PayPalBadge() {
  return (
    <CardChassis title="PayPal">
      <text x="23" y="20" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
        fontSize="11" fontWeight="700" fontStyle="italic">
        <tspan fill="#003087">Pay</tspan><tspan fill="#009CDE">Pal</tspan>
      </text>
    </CardChassis>
  );
}

const ALL_BRANDS = [VisaBadge, MastercardBadge, AmexBadge, DiscoverBadge, ApplePayBadge, PayPalBadge];

/**
 * A row of accepted-payment card badges.
 * @param {boolean} wallets  include Apple Pay + PayPal (default true)
 */
export function PaymentMethods({ wallets = true, className = '' }) {
  const brands = wallets ? ALL_BRANDS : ALL_BRANDS.slice(0, 4);
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {brands.map((Brand, i) => <Brand key={i} />)}
    </div>
  );
}

/* ── Scalloped medallion seal ─────────────────────────────────────────────── */
function scallopPath(cx, cy, bumps, outer, inner) {
  let d = '';
  const steps = bumps * 2;
  for (let i = 0; i < steps; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / bumps) * i - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    d += `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
  }
  return d + 'Z';
}

function Seal({ children, color = '#be185d' }) {
  const edge = scallopPath(32, 32, 24, 30, 26.5);
  return (
    <svg viewBox="0 0 64 64" className="w-16 h-16" aria-hidden="true">
      <path d={edge} fill={color} fillOpacity="0.10" />
      <path d={edge} fill="none" stroke={color} strokeOpacity="0.45" strokeWidth="1" />
      <circle cx="32" cy="32" r="23" fill="#fff" stroke={color} strokeOpacity="0.55"
        strokeWidth="1.4" strokeDasharray="1.5 2.5" />
      <g transform="translate(20,20)" stroke={color} fill="none" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round">
        {children}
      </g>
    </svg>
  );
}

const MEDALLIONS = [
  {
    color: '#be185d',
    label: '100% Secure Checkout',
    sub: '256-bit SSL encrypted',
    icon: <path d="M6 11V8a6 6 0 1 1 12 0v3M5 11h14v9H5zM12 15v2" />,
  },
  {
    color: '#9d174d',
    label: '30-Day Money-Back',
    sub: 'Love it or your money back',
    icon: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v4h4" /></>,
  },
  {
    color: '#db2777',
    label: 'Guaranteed Shipping',
    sub: 'Tracked, insured & on time',
    icon: <><path d="M1 6h13v9H1zM14 9h4l3 3v3h-7z" /><circle cx="6" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></>,
  },
];

/**
 * A row of scalloped guarantee seals. Wraps to a grid on narrow columns.
 * @param {boolean} onDark  render light labels for a dark background (e.g. footer)
 */
export function GuaranteeMedallions({ className = '', onDark = false }) {
  const labelCls = onDark ? 'text-gray-100' : 'text-gray-800 dark:text-gray-100';
  const subCls = onDark ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400';
  return (
    <div className={`grid grid-cols-3 gap-3 ${className}`}>
      {MEDALLIONS.map(m => (
        <div key={m.label} className="flex flex-col items-center text-center">
          <Seal color={m.color}>{m.icon}</Seal>
          <p className={`text-[12px] font-bold leading-tight mt-1 ${labelCls}`}>{m.label}</p>
          <p className={`text-[10px] leading-tight ${subCls}`}>{m.sub}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * The "Guaranteed safe & secure checkout" lock strip + accepted-payment row.
 * Designed to sit directly under a checkout CTA.
 */
export function SecureCheckoutBar({ className = '' }) {
  return (
    <div className={`rounded-xl border border-tea-200 bg-tea-50/60 dark:border-slate-700 dark:bg-slate-800/40 p-3.5 ${className}`}>
      <div className="flex items-center justify-center gap-2 text-[13px] font-semibold text-gray-700 dark:text-gray-200">
        <svg className="w-4 h-4 text-tea-600 dark:text-tea-400" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="11" width="14" height="9" rx="1.5" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        Guaranteed safe &amp; secure checkout
      </div>
      <PaymentMethods className="justify-center mt-3" />
      <p className="text-[11px] text-center text-gray-500 dark:text-gray-400 mt-2.5">
        Your payment is encrypted &amp; processed securely by Stripe. We never store your card details.
      </p>
    </div>
  );
}
